import { Box } from '@mui/material';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import { TrlFormSectionSeparator } from '»components/atoms';
import {
  TrlDatePicker,
  TrlFormDropdown,
  TrlTextField,
} from '»components/atoms/Inputs';
import { TrlFormRadioGroup } from '»components/molecules/TrlFormRadioGroup';
import {
  isAureaActiveClient,
  isBfacmActiveClient,
  isHemeraActiveClient,
} from '»utils/clientsUtils';
import { useFieldPropertyVerification } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { onboardingMainFormFieldsSelector } from '../../redux';

export const LegalPersonFields = () => {
  const mainFormFields = useSelector(onboardingMainFormFieldsSelector);

  const { verifyFieldProperty } = useFieldPropertyVerification(mainFormFields);

  const { values, setFieldValue } = useFormikContext();

  const yesLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-YES',
  });

  const noLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-NO',
  });

  const fieldsApiInitialValue = mainFormFields.reduce((acc, field) => {
    if (field.name !== 'representatives' && field.name !== 'partners')
      acc[field.name] = field.apiInitialValue;

    return acc;
  }, {});

  const hasInitialValue = (fieldName) =>
    Array.isArray(fieldsApiInitialValue[fieldName])
      ? fieldsApiInitialValue[fieldName].length !== 0
      : fieldsApiInitialValue[fieldName] !== '';

  const getFieldOptions = (currentFieldName) => {
    const currentField = mainFormFields.find(
      (field) => field.name === currentFieldName,
    );

    return currentField?.options ?? [];
  };

  const isFieldHidden = {
    usTaxId: verifyFieldProperty({
      fieldName: 'usTaxId',
      fieldProperty: 'isHidden',
    }),
    stockExchange: verifyFieldProperty({
      fieldName: 'stockExchange',
      fieldProperty: 'isHidden',
    }),
    ordTransEmail: verifyFieldProperty({
      fieldName: 'ordTransEmail',
      fieldProperty: 'isHidden',
    }),
    ordTransEmail2: verifyFieldProperty({
      fieldName: 'ordTransEmail2',
      fieldProperty: 'isHidden',
    }),
  };

  return (
    <>
      <TrlFormSectionSeparator
        title={TrlFormattedMessagePlain({
          id: 'TRLT_UI-IDENTIFICATION',
        })}
      />
      <Box className="flexWrap">
        <TrlTextField
          name="companyName"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-COMPANY_NAME' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-COMPANY_NAME' })}
          id="companyName"
          autoComplete="organization"
          isLocked={hasInitialValue('companyName')}
          isHidden={verifyFieldProperty({
            fieldName: 'companyName',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'companyName',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="denomination"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-DENOMINATION' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-DENOMINATION' })}
          id="denomination"
          autoComplete="organization"
          isLocked={hasInitialValue('denomination')}
          isHidden={verifyFieldProperty({
            fieldName: 'denomination',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'denomination',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlDatePicker
          name="constitutionDate"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-INCORPORATION_DATE' })}
          value={values.constitutionDate}
          disableFuture
          isLocked={hasInitialValue('constitutionDate')}
          dateFormatType="date"
          isHidden={verifyFieldProperty({
            fieldName: 'constitutionDate',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'constitutionDate',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="comercialRegisterNum"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-REG_NUMBER' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-REG_NUMBER' })}
          id="comercialRegisterNum"
          isLocked={hasInitialValue('comercialRegisterNum')}
          isHidden={verifyFieldProperty({
            fieldName: 'comercialRegisterNum',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'comercialRegisterNum',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="conservatory"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-CONSERVATORY' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-CONSERVATORY' })}
          id="conservatory"
          isLocked={hasInitialValue('conservatory')}
          isHidden={verifyFieldProperty({
            fieldName: 'conservatory',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'conservatory',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlFormDropdown
          name="society"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SOCIETY',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SOCIETY',
          })}
          isLocked={hasInitialValue('society')}
          options={getFieldOptions('society')}
          isHidden={verifyFieldProperty({
            fieldName: 'society',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'society',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="otherSociety"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-OTHER_SOCIETY' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-OTHER_SOCIETY',
          })}
          id="otherSociety"
          isLocked={hasInitialValue('otherSociety')}
          isHidden={verifyFieldProperty({
            fieldName: 'otherSociety',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'otherSociety',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="nif"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-TAX_ID' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-TAX_ID' })}
          id="nif"
          isLocked={hasInitialValue('nif')}
          isHidden={verifyFieldProperty({
            fieldName: 'nif',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'nif',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="cae"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ACTIVITY_CODE' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ACTIVITY_CODE',
          })}
          id="cae"
          isLocked={hasInitialValue('cae')}
          isHidden={verifyFieldProperty({
            fieldName: 'cae',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'cae',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="secundaryCae"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-SECUNDARY_CAE' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SECUNDARY_CAE',
          })}
          id="secundaryCae"
          isLocked={hasInitialValue('secundaryCae')}
          isHidden={verifyFieldProperty({
            fieldName: 'secundaryCae',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'secundaryCae',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="sectoralCode"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-SECTORAL_CODE' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SECTORAL_CODE',
          })}
          id="sectoralCode"
          isLocked={hasInitialValue('sectoralCode')}
          isHidden={verifyFieldProperty({
            fieldName: 'sectoralCode',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'sectoralCode',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="budgetUnit"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-BUDGET_UNIT' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-BUDGET_UNIT',
          })}
          id="budgetUnit"
          isLocked={hasInitialValue('budgetUnit')}
          isHidden={verifyFieldProperty({
            fieldName: 'budgetUnit',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'budgetUnit',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlFormRadioGroup
          name="companyInFormation"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-COMPANY_INFO_FORMATION',
          })}
          options={[
            { label: yesLabel, id: 'Y' },
            { label: noLabel, id: 'N' },
          ]}
          isLocked={hasInitialValue('companyInFormation')}
          isHidden={verifyFieldProperty({
            fieldName: 'companyInFormation',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'companyInFormation',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlFormRadioGroup
          name="exportingCompany"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-EXPORTING_COMPANY',
          })}
          options={[
            { label: yesLabel, id: 'Y' },
            { label: noLabel, id: 'N' },
          ]}
          isLocked={hasInitialValue('exportingCompany')}
          isHidden={verifyFieldProperty({
            fieldName: 'exportingCompany',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'exportingCompany',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="foreignCapital"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-FOREIGN_CAPITAL' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-FOREIGN_CAPITAL',
          })}
          id="foreignCapital"
          isLocked={hasInitialValue('foreignCapital')}
          isHidden={verifyFieldProperty({
            fieldName: 'foreignCapital',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'foreignCapital',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 3,
          }}
        />
        <TrlTextField
          name="shareCapital"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-SHARE_CAPITAL' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SHARE_CAPITAL',
          })}
          id="shareCapital"
          isLocked={hasInitialValue('shareCapital')}
          isHidden={verifyFieldProperty({
            fieldName: 'shareCapital',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'shareCapital',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 11,
          }}
        />
        <TrlTextField
          name="currency"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-CURRENCY' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-CURRENCY' })}
          id="currency"
          isLocked={hasInitialValue('currency')}
          isHidden={verifyFieldProperty({
            fieldName: 'currency',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'currency',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 2,
          }}
        />
        <TrlTextField
          name="employees"
          type="number"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-EMPLOYEES' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-EMPLOYEES' })}
          id="employees"
          isLocked={hasInitialValue('employees')}
          isHidden={verifyFieldProperty({
            fieldName: 'employees',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'employees',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 6,
          }}
        />
        <TrlTextField
          name="salesVolume"
          type="number"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-SALES_VOLUME' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-SALES_VOLUME' })}
          id="salesVolume"
          isLocked={hasInitialValue('salesVolume')}
          isHidden={verifyFieldProperty({
            fieldName: 'salesVolume',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'salesVolume',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="mainActivity"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-MAIN_ACTIVITY' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-MAIN_ACTIVITY',
          })}
          id="mainActivity"
          isLocked={hasInitialValue('mainActivity')}
          isHidden={verifyFieldProperty({
            fieldName: 'mainActivity',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'mainActivity',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="secondaryActivity"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-SECONDARY_ACTIVITY' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SECONDARY_ACTIVITY',
          })}
          id="secondaryActivity"
          isLocked={hasInitialValue('secondaryActivity')}
          isHidden={verifyFieldProperty({
            fieldName: 'secondaryActivity',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'secondaryActivity',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="tertiaryActivity"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-TERTIARY_ACTIVITY' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-TERTIARY_ACTIVITY',
          })}
          id="tertiaryActivity"
          isLocked={hasInitialValue('tertiaryActivity')}
          isHidden={verifyFieldProperty({
            fieldName: 'tertiaryActivity',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'tertiaryActivity',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="operationCountries"
          type="text"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-OPERATING_COUNTRIES',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-OPERATING_COUNTRIES',
          })}
          id="operationCountries"
          autoComplete="name"
          isLocked={hasInitialValue('operationCountries')}
          isHidden={verifyFieldProperty({
            fieldName: 'operationCountries',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'operationCountries',
            fieldProperty: 'isRequired',
          })}
        />
      </Box>

      <TrlFormSectionSeparator
        title={TrlFormattedMessagePlain({
          id: 'TRLT_UI-ADDRESS_CONTACTS',
        })}
      />
      <Box className="flexWrap">
        <TrlTextField
          name="street"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ADDRESS' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-ADDRESS' })}
          id="street"
          autoComplete="address-line1"
          isLocked={hasInitialValue('street')}
          isHidden={verifyFieldProperty({
            fieldName: 'street',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'street',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="secondaryAddress"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-SECONDARY_ADDRESS' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SECONDARY_ADDRESS',
          })}
          id="secondaryAddress"
          autoComplete="address-line2"
          isLocked={hasInitialValue('secondaryAddress')}
          isHidden={verifyFieldProperty({
            fieldName: 'secondaryAddress',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'secondaryAddress',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="town"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-CITY' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-CITY' })}
          id="town"
          isLocked={hasInitialValue('town')}
          isHidden={verifyFieldProperty({
            fieldName: 'town',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'town',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="province"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PROVINCE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-PROVINCE' })}
          id="province"
          isLocked={hasInitialValue('province')}
          isHidden={verifyFieldProperty({
            fieldName: 'province',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'province',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="postCode"
          type="text"
          autoComplete="postal-code"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-POST_CODE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-POST_CODE' })}
          id="postCode"
          isLocked={hasInitialValue('postCode')}
          isHidden={verifyFieldProperty({
            fieldName: 'postCode',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'postCode',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="country"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-COUNTRY' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-COUNTRY' })}
          id="country"
          autoComplete="country-name"
          isLocked={hasInitialValue('country')}
          isHidden={verifyFieldProperty({
            fieldName: 'country',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'country',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="telephoneNumIndicator"
          type="text"
          autoComplete="tel-country-code"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-TELEPHONE_NUM_INDICATOR',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-TELEPHONE_NUM_INDICATOR',
          })}
          id="telephoneNumIndicator"
          isLocked={hasInitialValue('telephoneNumIndicator')}
          isHidden={verifyFieldProperty({
            fieldName: 'telephoneNumIndicator',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'telephoneNumIndicator',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 5,
          }}
        />
        <TrlTextField
          name="telephoneNum"
          type="tel"
          autoComplete="tel"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-TELEPHONE_NUM' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-TELEPHONE_NUM',
          })}
          id="telephoneNum"
          isLocked={hasInitialValue('telephoneNum')}
          isHidden={verifyFieldProperty({
            fieldName: 'telephoneNum',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'telephoneNum',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="telephoneNumDescr"
          type="text"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-TELEPHONE_NUM_DESCR',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-TELEPHONE_NUM_DESCR',
          })}
          id="telephoneNumDescr"
          isLocked={hasInitialValue('telephoneNumDescr')}
          isHidden={verifyFieldProperty({
            fieldName: 'telephoneNumDescr',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'telephoneNumDescr',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="phoneNumIndicator"
          type="text"
          autoComplete="tel-country-code"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-PHONE_NUM_INDICATOR',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-PHONE_NUM_INDICATOR',
          })}
          id="phoneNumIndicator"
          isLocked={hasInitialValue('phoneNumIndicator')}
          isHidden={verifyFieldProperty({
            fieldName: 'phoneNumIndicator',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'phoneNumIndicator',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 5,
          }}
        />
        <TrlTextField
          name="phoneNum"
          type="tel"
          autoComplete="tel"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-MOBILE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-MOBILE' })}
          id="phoneNum"
          isLocked={hasInitialValue('phoneNum')}
          isHidden={verifyFieldProperty({
            fieldName: 'phoneNum',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'phoneNum',
            fieldProperty: 'isRequired',
          })}
          extraInfo={
            isAureaActiveClient
              ? TrlFormattedMessagePlain({ id: 'TRLT_UI-MOBILE_HINT' })
              : ''
          }
        />
        <TrlTextField
          name="phoneNumDescr"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PHONE_NUM_DESCR' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-PHONE_NUM_DESCR',
          })}
          id="phoneNumDescr"
          isLocked={hasInitialValue('phoneNumDescr')}
          isHidden={verifyFieldProperty({
            fieldName: 'phoneNumDescr',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'phoneNumDescr',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="email"
          type="email"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-EMAIL' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-EMAIL' })}
          id="email"
          isLocked={hasInitialValue('email')}
          isHidden={verifyFieldProperty({
            fieldName: 'email',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'email',
            fieldProperty: 'isRequired',
          })}
          extraInfo={
            isAureaActiveClient
              ? TrlFormattedMessagePlain({ id: 'TRLT_UI-EMAIL_HINT' })
              : ''
          }
        />
        <TrlTextField
          name="website"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-WEBSITE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-WEBSITE' })}
          id="website"
          isLocked={hasInitialValue('website')}
          isHidden={verifyFieldProperty({
            fieldName: 'website',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'website',
            fieldProperty: 'isRequired',
          })}
        />
      </Box>

      {isBfacmActiveClient && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({ id: 'TRLT_UI-FATCA' })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="americanCompany"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-AMERICAN_COMPANY',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('americanCompany')}
              isHidden={verifyFieldProperty({
                fieldName: 'americanCompany',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'americanCompany',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (event.target.value === 'N' && !isFieldHidden.usTaxId)
                  setFieldValue('usTaxId', '', false);
              }}
            />
            {values.americanCompany === 'Y' && (
              <TrlTextField
                name="usTaxId"
                type="text"
                label={TrlFormattedMessagePlain({ id: 'TRLT_UI-US_TAX_ID' })}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-US_TAX_ID',
                })}
                id="usTaxId"
                isLocked={hasInitialValue('usTaxId')}
                isHidden={isFieldHidden.usTaxId}
                isRequired={verifyFieldProperty({
                  fieldName: 'usTaxId',
                  fieldProperty: 'isRequired',
                })}
                inputProps={{
                  maxLength: 9,
                }}
              />
            )}
            <TrlFormRadioGroup
              name="nonUsFinancialCompany"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-NON_US_FINANCIAL_COMPANY',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('nonUsFinancialCompany')}
              isHidden={verifyFieldProperty({
                fieldName: 'nonUsFinancialCompany',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'nonUsFinancialCompany',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="giin"
              type="text"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-GIIN' })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-GIIN',
              })}
              id="giin"
              isLocked={hasInitialValue('giin')}
              isHidden={verifyFieldProperty({
                fieldName: 'giin',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'giin',
                fieldProperty: 'isRequired',
              })}
              inputProps={{
                maxLength: 19,
              }}
            />
            <TrlFormRadioGroup
              name="usPersonWithVotingRights"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-US_PERSON_WITH_VOTING_RIGHTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('usPersonWithVotingRights')}
              isHidden={verifyFieldProperty({
                fieldName: 'usPersonWithVotingRights',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'usPersonWithVotingRights',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="stockExcahangeListedCompany"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-STOCK_EXCHANGE_LISTED_COMPANY',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('stockExcahangeListedCompany')}
              isHidden={verifyFieldProperty({
                fieldName: 'stockExcahangeListedCompany',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'stockExcahangeListedCompany',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (event.target.value === 'N' && !isFieldHidden.stockExchange)
                  setFieldValue('stockExchange', '', false);
              }}
            />
            {values.stockExcahangeListedCompany === 'Y' && (
              <TrlTextField
                name="stockExchange"
                type="text"
                label={TrlFormattedMessagePlain({ id: 'TRLT_UI-WHICH' })}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-WHICH',
                })}
                id="stockExchange"
                isLocked={hasInitialValue('stockExchange')}
                isHidden={isFieldHidden.stockExchange}
                isRequired={verifyFieldProperty({
                  fieldName: 'stockExchange',
                  fieldProperty: 'isRequired',
                })}
              />
            )}
          </Box>
        </>
      )}

      {(isAureaActiveClient || isHemeraActiveClient) && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-SERVICES_PROVIDED',
            })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="financialInstr"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-FINANCIAL_INSTRUMENTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('financialInstr')}
              isHidden={verifyFieldProperty({
                fieldName: 'financialInstr',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'financialInstr',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="marketOrders"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-MARKET_ORDERS' })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('marketOrders')}
              isHidden={verifyFieldProperty({
                fieldName: 'marketOrders',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'marketOrders',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="fundsSource"
              type="text"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-FUND_ORIGIN' })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-FUND_ORIGIN',
              })}
              id="fundsSource"
              isLocked={hasInitialValue('fundsSource')}
              isHidden={verifyFieldProperty({
                fieldName: 'fundsSource',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'fundsSource',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="businessSource"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-BUSINESS_NATURE',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-BUSINESS_NATURE',
              })}
              id="businessSource"
              isLocked={hasInitialValue('businessSource')}
              isHidden={verifyFieldProperty({
                fieldName: 'businessSource',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'businessSource',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="regDepFinantialInstr"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-REG_CUSTODY_FIN_INSTRUMENTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('regDepFinantialInstr')}
              isHidden={verifyFieldProperty({
                fieldName: 'regDepFinantialInstr',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'regDepFinantialInstr',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                setFieldValue('recepExecOrders', event.target.value, false);
              }}
            />
          </Box>
        </>
      )}

      {isHemeraActiveClient && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-SEC_DERIVAT_INSTRUMENTS',
            })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="stocks"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-STOCKS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('stocks')}
              isHidden={verifyFieldProperty({
                fieldName: 'stocks',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'stocks',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="corporateObligations"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-CORPORATE_OBLIGATIONS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('corporateObligations')}
              isHidden={verifyFieldProperty({
                fieldName: 'corporateObligations',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'corporateObligations',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="treasuryBonds"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-TREASURY_BONDS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('treasuryBonds')}
              isHidden={verifyFieldProperty({
                fieldName: 'treasuryBonds',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'treasuryBonds',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="treasuryBills"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-TREASURY_BILLS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('treasuryBills')}
              isHidden={verifyFieldProperty({
                fieldName: 'treasuryBills',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'treasuryBills',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="collectiveInvestUnits"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-COLLECTIVE_INVESTUNITS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('collectiveInvestUnits')}
              isHidden={verifyFieldProperty({
                fieldName: 'collectiveInvestUnits',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'collectiveInvestUnits',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="depositCertificates"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-DEPOSIT_CERTIFICATES',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('depositCertificates')}
              isHidden={verifyFieldProperty({
                fieldName: 'depositCertificates',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'depositCertificates',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="derivativeInstruments"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-DERIVATIVE_INSTRUMENTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('derivativeInstruments')}
              isHidden={verifyFieldProperty({
                fieldName: 'derivativeInstruments',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'derivativeInstruments',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="structuredProducts"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-STRUCTURED_PRODUCTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('structuredProducts')}
              isHidden={verifyFieldProperty({
                fieldName: 'structuredProducts',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'structuredProducts',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="commercialPaper"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-COMMERCIAL_PAPER',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('commercialPaper')}
              isHidden={verifyFieldProperty({
                fieldName: 'commercialPaper',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'commercialPaper',
                fieldProperty: 'isRequired',
              })}
            />
          </Box>
        </>
      )}

      {isHemeraActiveClient && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-ORDER_TRANS_METHOD',
            })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="ordTransInPerson"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-IN_PERSON',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ordTransInPerson')}
              isHidden={verifyFieldProperty({
                fieldName: 'ordTransInPerson',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ordTransInPerson',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="ordTransHasEmail"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-EMAIL_ONBOARDING',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ordTransHasEmail')}
              isHidden={verifyFieldProperty({
                fieldName: 'ordTransHasEmail',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ordTransHasEmail',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (event.target.value === 'N') {
                  if (!isFieldHidden.ordTransEmail)
                    setFieldValue('ordTransEmail', '', false);
                  if (!isFieldHidden.ordTransEmail2)
                    setFieldValue('ordTransEmail2', '', false);
                }
              }}
            />
            {values.ordTransHasEmail === 'Y' && (
              <>
                <TrlTextField
                  name="ordTransEmail"
                  type="email"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-EMAIL_ADDRESS',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-EMAIL_ADDRESS',
                  })}
                  id="ordTransEmail"
                  isLocked={hasInitialValue('ordTransEmail')}
                  isHidden={isFieldHidden.ordTransEmail}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ordTransEmail',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name="ordTransEmail2"
                  type="email"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-ALTERNATIVE_EMAIL_ADDRESS',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-ALTERNATIVE_EMAIL_ADDRESS',
                  })}
                  id="ordTransEmail2"
                  isLocked={hasInitialValue('ordTransEmail2')}
                  isHidden={isFieldHidden.ordTransEmail2}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ordTransEmail2',
                    fieldProperty: 'isRequired',
                  })}
                />
              </>
            )}
            <TrlFormRadioGroup
              name="ordTransPhoneNum"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-PHONE_CONTACT',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ordTransPhoneNum')}
              isHidden={verifyFieldProperty({
                fieldName: 'ordTransPhoneNum',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ordTransPhoneNum',
                fieldProperty: 'isRequired',
              })}
            />
          </Box>
        </>
      )}
    </>
  );
};
